import { isExplicitDesiredDate } from 'src/stores/_infrastructure/desiredDateFilter';
import { useFilterStore } from 'src/stores/filter';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { useMailListStore } from 'src/stores/mailList';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { DateRangeSelection } from 'src/models/common';
import { ActivityListTypeEnum, MODULE_LIST_TYPE } from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { StatusEnum } from 'src/models/status/enums';

import { today } from 'src/util/date';
import { registerListInstanceFetcher, resolveListInstance } from 'src/util/listInstance/listInstanceFetchDispatch';
import { registerModuleTabSettingsReset } from 'src/util/listInstance/moduleTabSettingsReset';
import { QueryStore } from 'src/util/query/QueryStore';

const warmupMailList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	}
) => {
	const mailListStore = useMailListStore();

	void mailListStore.fetchFilterBoxes({
		queryStore: queryStore,
	});

	void mailListStore.fetchFilterUsers({
		queryStore: queryStore,
	});

	void fetchMailList(_store, queryStore, {
		...queryParameters,
		clearResultsBeforeFetch: false,
	});
};

const fetchMailList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		clearResultsBeforeFetch: boolean;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	},
	listStore: ReturnType<typeof useMailListStore> = useMailListStore()
) => {
	const hasActiveSearch = queryParameters.searchTerm.trim() !== '' || queryParameters.hiddenSearchTerm.trim() !== '';
	const didClearSubscriptionsForSearch = hasActiveSearch;

	if (isExplicitDesiredDate(queryParameters.desiredDate)) {
		listStore.setDateFilterAction({
			dateFilter: queryParameters.desiredDate,
		});
	}

	if (hasActiveSearch) {
		listStore.clearSubscriptions({
			queryStore: queryStore,
		});
	}

	if (queryParameters.clearResultsBeforeFetch) {
		if (
			queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}

			listStore.silentSetOpenMailList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.MINE ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}

			listStore.silentSetMineMailList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.ALL ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}

			listStore.silentSetAllMailList();
		}
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchOpenMailList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.MINE ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchMineMailList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.ALL ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}

		void listStore.fetchAllMailList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}
};

registerListInstanceFetcher('mailList', ({ listStore, params, queryStore, runtimeStore }) =>
	fetchMailList(runtimeStore as never, queryStore as never, params as never, listStore as never)
);

// Task 2.2 (analog Task 2.1 / taskList): Basis-Identifier der drei Sub-Listen-Scroll-Positionen
// (Offen/Meine/Alle) — muss `MailList.vue`s `virtual-list-id="moduleActivityListScrollerMails"`
// spiegeln (dort mit Open/Mine/All-Suffix versehen, s. GenericActivityListTabPanel.vue). Kein
// zentraler Export vorhanden, daher hier dieselbe Literal-Konstante wie in MailList.vue.
const MAIL_LIST_SCROLL_BASE_IDENTIFIER = 'moduleActivityListScrollerMails';

// Nur die Default-Instanz (Modul-Tabs laufen immer auf ihr) — `?list=`-Instanz-Tabs disposen
// bereits vollständig über useListInstanceLifecycle und laufen nie durch diesen Reset (Task 1.7 im
// Layout-Hook filtert sie über tab.query.list aus). Feldliste 1:1 aus clearAllFilters (FilterComponentActions.ts)
// für 'mailList' abgeleitet, OHNE deren router.replace/refreshTheList: Box-/Benutzer-Filter, Datumsfilter
// (+ Zeitraum-Preset), Richtungsfilter (mailList IST ein isDirectionRoute()-Ziel, anders als taskList/noteList),
// gewählte Shared-Query, Such-/Hidden-Suchbegriff, Mehrfachauswahl, Scroll-Positionen der drei Sub-Listen,
// aktive Ansicht.
// NICHT angefasst: sort/listViewModeLarge (persistierte Präferenzen), Datenlisten + Counts + Pagination
// + queryStoreSubscriptions (Menü-Badges/Warmup lesen den Default-Store weiter — kein $reset/$dispose).
const resetMailListModuleTabSettings = (): void => {
	const mailListStore = useMailListStore();
	const filterStore = useFilterStore();
	const genericActivityListStore = useGenericActivityListStore();

	mailListStore.setFilteredBoxesAction({ boxIdFilter: [] });
	mailListStore.setFilteredUsersAction({ filterUsers: [] });
	mailListStore.setDateFilterAction({ dateFilter: today() });
	mailListStore.setDateTimeRangeFilter({ dateTimeRangeFilter: PreconfiguredTimeRanges.TODAY });
	mailListStore.setDirectionFilterAction({ directionFilter: 'combined' });
	mailListStore.setSelectedSharedQueryAction({ selectedSharedQuery: null });

	filterStore.setSearchTerm({ module: 'mailList', searchTerm: '' });
	filterStore.setHiddenSearchTerm({ hiddenSearchTerm: '', module: 'mailList' });

	genericActivityListStore.clearTheSelection({ genericListIdentifier: 'mailList' });
	genericActivityListStore.resetScrollPositions(MAIL_LIST_SCROLL_BASE_IDENTIFIER);
	genericActivityListStore.setActiveView({ activeView: MODULE_LIST_TYPE.OPEN, genericListIdentifier: 'mailList' });
};

registerModuleTabSettingsReset('mailList', resetMailListModuleTabSettings);

const deleteTheSelectedMailAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: { aggregateIds: string[]; genericListIdentifier?: string; processId: string }
) => {
	const genericActivityListStore = useGenericActivityListStore();
	const resolved = resolveListInstance(queryParameters.genericListIdentifier);

	// 'missing' (Instanz-Key ohne Registry-Eintrag = Tab bereits geschlossen) → NICHT den Default-Store
	// mutieren; 'instance' → Store DIESER Listen-Instanz; 'default' → Default-Store (byte-identisch zu vorher).
	if (resolved.outcome !== 'missing') {
		const mailListStore: ReturnType<typeof useMailListStore> =
			resolved.outcome === 'instance' ? (resolved.listStore as never) : useMailListStore();

		mailListStore.deleteSelectedAggregateIds({
			aggregateIds: queryParameters.aggregateIds,
		});
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: queryParameters['genericListIdentifier'] ?? 'mailList',
	});
};

const spamTheSelectedMailAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: { aggregateIds: string[]; genericListIdentifier?: string; processId: string }
) => {
	const genericActivityListStore = useGenericActivityListStore();
	const resolved = resolveListInstance(queryParameters.genericListIdentifier);

	// 'missing' (Instanz-Key ohne Registry-Eintrag = Tab bereits geschlossen) → NICHT den Default-Store
	// mutieren; 'instance' → Store DIESER Listen-Instanz; 'default' → Default-Store (byte-identisch zu vorher).
	if (resolved.outcome !== 'missing') {
		const mailListStore: ReturnType<typeof useMailListStore> =
			resolved.outcome === 'instance' ? (resolved.listStore as never) : useMailListStore();

		mailListStore.spamSelectedAggregateIds({
			aggregateIds: queryParameters.aggregateIds,
		});
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: queryParameters['genericListIdentifier'] ?? 'mailList',
	});
};

const setTheStateForTheSelectedMailAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: {
		aggregateIds: string[];
		genericListIdentifier?: string;
		state: StatusEnum;
	}
) => {
	const genericActivityListStore = useGenericActivityListStore();
	const resolved = resolveListInstance(queryParameters.genericListIdentifier);

	// 'missing' (Instanz-Key ohne Registry-Eintrag = Tab bereits geschlossen) → NICHT den Default-Store
	// mutieren; 'instance' → Store DIESER Listen-Instanz; 'default' → Default-Store (byte-identisch zu vorher).
	if (resolved.outcome !== 'missing') {
		const mailListStore: ReturnType<typeof useMailListStore> =
			resolved.outcome === 'instance' ? (resolved.listStore as never) : useMailListStore();

		switch (queryParameters.state) {
			case StatusEnum.UNSEEN:
				mailListStore.setTheMailStateToUnseen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.SEEN:
				mailListStore.setTheMailStateToSeen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.READY:
				mailListStore.setTheMailStateToReady({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			default:
				break;
		}
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: queryParameters['genericListIdentifier'] ?? 'mailList',
	});
};

export {
	deleteTheSelectedMailAggregateIds,
	fetchMailList,
	setTheStateForTheSelectedMailAggregateIds,
	spamTheSelectedMailAggregateIds,
	warmupMailList,
};
